if (!customElements.get('free-shipping-upsell')) {
  class FreeShippingUpsell extends HTMLElement {
    constructor() {
      super();
      this.products = [];
      this.cart = null;
      this.maxProducts = 3;
    }

    connectedCallback() {
      this.minimum = Number(this.dataset.minimum) || 0;
      this.cartTotal = Number(this.dataset.cartTotal) || 0;
      this.currency = this.dataset.currency || Shopify.currency.active;
      this.locale = this.dataset.locale || Shopify.locale || document.documentElement.lang || 'da-DK';
      this.cartUrl = this.dataset.cartUrl || theme.routes.cart;
      this.cartAddUrl = this.withJsExtension(this.dataset.cartAddUrl || theme.routes.cartAdd);
      this.recommendationsBaseUrl = this.dataset.recommendationsBaseUrl || '/recommendations/products';
      this.currentProductId = Number(this.dataset.currentProductId) || 0;
      this.recommendationProductId = this.currentProductId;

      this.textEl = this.querySelector('[data-free-shipping-upsell-text]');
      this.hintEl = this.querySelector('[data-free-shipping-upsell-hint]');
      this.progressEl = this.querySelector('[data-free-shipping-upsell-progress]');
      this.barEl = this.querySelector('[data-free-shipping-upsell-bar]');
      this.productsEl = this.querySelector('[data-free-shipping-upsell-products]');
      this.errorEl = this.querySelector('[data-free-shipping-upsell-error]');

      this.handleCartEvent = this.handleCartEvent.bind(this);
      document.addEventListener('on:cart:add', this.handleCartEvent);
      document.addEventListener('on:line-item:change', this.handleCartEvent);

      this.renderProgress();
      this.init();
    }

    disconnectedCallback() {
      document.removeEventListener('on:cart:add', this.handleCartEvent);
      document.removeEventListener('on:line-item:change', this.handleCartEvent);
    }

    async init() {
      await this.refreshCart();
      await this.refreshRecommendations();
      this.render();
    }

    async refreshCart() {
      try {
        const response = await fetch(`${this.cartUrl}.js`);
        if (!response.ok) throw new Error(response.status);
        this.cart = await response.json();
        this.cartTotal = this.cart.total_price || 0;
      } catch (error) {
        console.log(error); // eslint-disable-line no-console
      }
    }

    async refreshRecommendations() {
      const productId = this.getRecommendationProductId();
      await this.fetchRecommendations(productId);
      this.recommendationProductId = productId;
    }

    async fetchRecommendations(productId = this.currentProductId) {
      const [products, relatedProducts] = await Promise.all([
        this.fetchRecommendationUrl(this.getRecommendationUrl(productId, 'complementary')),
        this.fetchRecommendationUrl(this.getRecommendationUrl(productId, 'related'))
      ]);

      const seenProductIds = new Set();
      this.products = [...products, ...relatedProducts].filter((product) => {
        const productId = Number(product.id);
        if (!productId || productId === this.currentProductId || seenProductIds.has(productId)) return false;
        seenProductIds.add(productId);
        return this.getAvailableVariant(product);
      });
    }

    async fetchRecommendationUrl(url) {
      if (!url) return [];

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(response.status);
        const data = await response.json();
        return Array.isArray(data.products) ? data.products : [];
      } catch (error) {
        console.log(error); // eslint-disable-line no-console
        return [];
      }
    }

    async handleCartEvent(event) {
      if (event.detail && event.detail.cart) {
        this.cart = event.detail.cart;
        this.cartTotal = event.detail.cart.total_price || 0;
        await this.refreshRecommendations();
        this.render();
        return;
      }

      await this.refreshCart();
      await this.refreshRecommendations();
      this.render();
    }

    render() {
      this.renderProgress();
      this.renderProducts();
    }

    renderProgress() {
      const remaining = Math.max(this.minimum - this.cartTotal, 0);
      const progress = this.minimum > 0 ? Math.min((this.cartTotal / this.minimum) * 100, 100) : 0;

      if (this.textEl) {
        this.textEl.textContent = remaining > 0
          ? `Kun ${this.formatMoney(remaining)} fra gratis levering`
          : 'Gratis levering er låst op 🎉';
      }

      if (this.hintEl) {
        this.hintEl.textContent = remaining > 0
          ? 'Tilføj et populært valg nedenfor og kom tættere på fri fragt.'
          : 'Din ordre kvalificerer sig nu til fri fragt.';
      }

      if (this.barEl) {
        this.barEl.style.width = `${progress}%`;
      }

      if (this.progressEl) {
        this.progressEl.setAttribute('aria-valuenow', Math.round(progress));
      }

      this.classList.toggle('is-complete', remaining === 0);
    }

    renderProducts() {
      if (!this.productsEl) return;

      if (this.getRemainingAmount() <= 0) {
        this.productsEl.hidden = true;
        this.productsEl.innerHTML = '';
        return;
      }

      const visibleProducts = this.getVisibleProducts();

      this.productsEl.hidden = visibleProducts.length === 0;
      this.productsEl.innerHTML = '';

      const heading = document.createElement('p');
      heading.className = 'free-shipping-upsell__products-heading';
      heading.textContent = this.getRemainingAmount() > 0
        ? 'Tilføj og få gratis levering'
        : 'Populære tilvalg';
      this.productsEl.appendChild(heading);

      visibleProducts.forEach((product) => {
        this.productsEl.appendChild(this.createProductCard(product));
      });
    }

    getVisibleProducts() {
      const remaining = this.getRemainingAmount();
      const candidates = this.products.filter((product) => !this.isProductInCart(product));
      if (remaining <= 0) return candidates.slice(0, this.maxProducts);

      const unlockingProducts = candidates
        .filter((product) => this.getProductPrice(product) >= remaining)
        .sort((a, b) => this.getProductPrice(a) - this.getProductPrice(b));

      if (unlockingProducts.length >= this.maxProducts) {
        return unlockingProducts.slice(0, this.maxProducts);
      }

      const closerProducts = candidates
        .filter((product) => this.getProductPrice(product) < remaining)
        .sort((a, b) => this.getProductPrice(b) - this.getProductPrice(a));

      return [...unlockingProducts, ...closerProducts].slice(0, this.maxProducts);
    }

    createProductCard(product) {
      const variant = this.getAvailableVariant(product);
      const card = document.createElement('article');
      card.className = 'free-shipping-upsell__product';

      const image = this.getProductImage(product);
      if (image) {
        const imageEl = document.createElement('img');
        imageEl.className = 'free-shipping-upsell__image';
        imageEl.src = image;
        imageEl.alt = product.title || '';
        imageEl.loading = 'lazy';
        card.appendChild(imageEl);
      }

      const info = document.createElement('div');
      info.className = 'free-shipping-upsell__product-info';

      const title = document.createElement('a');
      title.className = 'free-shipping-upsell__title';
      title.href = product.url || '#';
      title.textContent = product.title || '';
      info.appendChild(title);

      const price = document.createElement('span');
      price.className = 'free-shipping-upsell__price';
      price.textContent = this.formatMoney(this.getProductPrice(product, variant));
      info.appendChild(price);

      const badge = this.createConversionBadge(product, variant);
      if (badge) info.appendChild(badge);
      card.appendChild(info);

      const button = document.createElement('button');
      button.className = 'free-shipping-upsell__button btn btn--secondary';
      button.type = 'button';
      button.dataset.variantId = variant.id;
      button.textContent = 'Tilføj';
      button.addEventListener('click', () => this.addVariant(button, variant.id));
      card.appendChild(button);

      return card;
    }

    createConversionBadge(product, variant) {
      const remaining = this.getRemainingAmount();
      if (remaining <= 0) return null;

      const price = this.getProductPrice(product, variant);
      if (!price) return null;

      const badge = document.createElement('span');
      badge.className = 'free-shipping-upsell__badge';
      badge.textContent = price >= remaining ? 'Giver gratis levering' : `+${this.formatMoney(price)} tættere på`;
      return badge;
    }

    getRemainingAmount() {
      return Math.max(this.minimum - this.cartTotal, 0);
    }

    async addVariant(button, variantId) {
      if (!variantId || button.getAttribute('aria-disabled') === 'true') return;

      const originalText = button.textContent;
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('is-loading');
      this.setError();

      try {
        const oldCart = this.cart || await this.fetchCartData();
        const sections = ['cart-icon-bubble'];
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          sections.push(cartDrawer.closest('.shopify-section').id.replace('shopify-section-', ''));
        }

        const response = await fetch(this.cartAddUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections,
            sections_url: window.location.pathname
          })
        });
        const responseText = await response.text();
        const data = this.parseJsonResponse(responseText);
        if (!response.ok) throw new Error(data.description || data.message || response.status);

        if (data.sections) {
          this.updateCartIcon(data);
        }

        if (cartDrawer && data.sections) {
          cartDrawer.renderContents(data, theme.settings.afterAtc === 'drawer');
        } else if (cartDrawer) {
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
          if (theme.settings.afterAtc === 'drawer') {
            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
          }
        }

        const newCart = await this.fetchCartData();
        this.cart = newCart;
        this.cartTotal = newCart.total_price || 0;
        this.dispatchCartEvent(oldCart, newCart, Number(variantId));
        this.render();

        button.textContent = 'Tilføjet';
        button.classList.add('is-success');
      } catch (error) {
        console.log(error); // eslint-disable-line no-console
        this.setError(error.message);
        button.textContent = originalText;
      } finally {
        button.classList.remove('is-loading');
        setTimeout(() => {
          button.removeAttribute('aria-disabled');
          button.classList.remove('is-success');
          button.textContent = originalText;
        }, 1200);
      }
    }

    async fetchCartData() {
      const response = await fetch(`${this.cartUrl}.js`);
      if (!response.ok) throw new Error(response.status);
      return response.json();
    }

    dispatchCartEvent(oldCart, newCart, variantId) {
      const oldItem = oldCart && oldCart.items
        ? oldCart.items.find((item) => item.variant_id === variantId)
        : null;
      const newItem = newCart.items.find((item) => item.variant_id === variantId);

      this.dispatchEvent(new CustomEvent(oldItem ? 'on:line-item:change' : 'on:cart:add', {
        bubbles: true,
        detail: {
          cart: newCart,
          variantId,
          oldQuantity: oldItem ? oldItem.quantity : 0,
          newQuantity: newItem ? newItem.quantity : 1
        }
      }));
    }

    getRecommendationProductId() {
      if (!this.cart || !Array.isArray(this.cart.items) || this.cart.items.length === 0) {
        return this.currentProductId;
      }

      const mostRecentVisibleItem = this.cart.items.find((item) => item.product_id !== this.currentProductId);
      return mostRecentVisibleItem ? mostRecentVisibleItem.product_id : this.cart.items[0].product_id;
    }

    getRecommendationUrl(productId, intent) {
      const baseUrl = this.withJsonExtension(this.recommendationsBaseUrl);
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.set('product_id', productId || this.currentProductId);
      url.searchParams.set('limit', '10');
      url.searchParams.set('intent', intent);
      return url.toString();
    }

    updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble && response.sections && response.sections['cart-icon-bubble']) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    isProductInCart(product) {
      if (!this.cart || !Array.isArray(this.cart.items)) return false;
      const productId = Number(product.id);
      return this.cart.items.some((item) => item.product_id === productId);
    }

    getAvailableVariant(product) {
      if (!product || !Array.isArray(product.variants)) return null;
      return product.variants.find((variant) => variant.available) || null;
    }

    getProductPrice(product, variant = this.getAvailableVariant(product)) {
      return Number(product.price || (variant && variant.price) || 0);
    }

    getProductImage(product) {
      if (!product) return null;
      if (typeof product.featured_image === 'string') return product.featured_image;
      if (product.featured_image && product.featured_image.url) return product.featured_image.url;
      if (Array.isArray(product.images) && product.images.length > 0) return product.images[0];
      return null;
    }

    setError(message = '') {
      if (!this.errorEl) return;
      this.errorEl.hidden = !message;
      this.errorEl.textContent = message;
    }

    formatMoney(cents) {
      return (cents / 100).toLocaleString(this.locale, {
        style: 'currency',
        currency: this.currency
      });
    }

    parseJsonResponse(text) {
      if (!text || !text.trim()) return {};

      try {
        return JSON.parse(text);
      } catch (error) {
        console.log(error); // eslint-disable-line no-console
        return {};
      }
    }

    withJsExtension(url) {
      if (!url || url.endsWith('.js')) return url;
      return `${url}.js`;
    }

    withJsonExtension(url) {
      if (!url || url.endsWith('.json')) return url;
      return `${url}.json`;
    }
  }

  customElements.define('free-shipping-upsell', FreeShippingUpsell);
}
